import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { FetchHttpClient } from '@/infra/http/fetch-http-client.js';
import { makeAuthController } from '@/main/factories/controllers/auth-controller.js';
import { makeClientController } from '@/main/factories/controllers/client-controller.js';
import { makeDevelopmentController, makeDevelopmentDetailController } from '@/main/factories/controllers/development-controller.js';
import { makeDashboardController } from '@/main/factories/controllers/misc-controllers.js';
import { makeNotificationController } from '@/main/factories/controllers/misc-controllers.js';
import { makeReasonController } from '@/main/factories/controllers/misc-controllers.js';
import { makeProjectController } from '@/main/factories/controllers/project-controller.js';
import { makeSapConnectionController } from '@/main/factories/controllers/sap-connection-controller.js';
import { makeThemeController } from '@/main/factories/controllers/theme-controller.js';
import { makeUserController } from '@/main/factories/controllers/user-controller.js';
import { makeAiChatRepository } from '@/main/factories/repositories/ai-chat-repository.js';
import { getListAiModelsUseCase } from '@/main/factories/use-cases/ai-models.js';
import { AppearancePage } from '@/presentation/pages/appearance/appearance-page.js';
import { ForgotPasswordPage } from '@/presentation/pages/auth/forgot-password-page.js';
import { LoginPage } from '@/presentation/pages/auth/login-page.js';
import { MicrosoftLoginPage } from '@/presentation/pages/auth/microsoft-login-page.js';
import { ResetPasswordPage } from '@/presentation/pages/auth/reset-password-page.js';
import { ClientListPage } from '@/presentation/pages/clients/client-list-page.js';
import { DashboardPage } from '@/presentation/pages/dashboard/dashboard-page.js';
import { DevelopmentDetailPage } from '@/presentation/pages/developments/development-detail/development-detail-page.js';
import { DevelopmentFormPage } from '@/presentation/pages/developments/development-form-page.js';
import { DevelopmentListPage } from '@/presentation/pages/developments/development-list-page.js';
import { NotificationsPage } from '@/presentation/pages/notifications/notifications-page.js';
import { MyProfilePage } from '@/presentation/pages/profile/my-profile-page.js';
import { ProjectListPage } from '@/presentation/pages/projects/project-list-page.js';
import { ReasonListPage } from '@/presentation/pages/reasons/reason-list-page.js';
import { ReportsPage } from '@/presentation/pages/reports/reports-page.js';
import { UserListPage } from '@/presentation/pages/users/user-list-page.js';
import { ROUTES } from '@/shared/constants/routes.js';

const httpClient = new FetchHttpClient();

export const LoginPageFactory: React.FC = () => {
    const controller = useMemo(() => makeAuthController(httpClient), []);
    return <LoginPage controller={controller} />;
};

export const MicrosoftLoginPageFactory: React.FC = () => {
    const controller = useMemo(() => makeAuthController(httpClient), []);
    return <MicrosoftLoginPage controller={controller} />;
};

export const ForgotPasswordPageFactory: React.FC = () => {
    const controller = useMemo(() => makeAuthController(httpClient), []);
    return <ForgotPasswordPage controller={controller} />;
};

export const ResetPasswordPageFactory: React.FC = () => {
    const controller = useMemo(() => makeAuthController(httpClient), []);
    return <ResetPasswordPage controller={controller} mode="reset" />;
};

export const FirstAccessPageFactory: React.FC = () => {
    const controller = useMemo(() => makeAuthController(httpClient), []);
    return <ResetPasswordPage controller={controller} mode="first-access" />;
};

export const DashboardPageFactory: React.FC = () => {
    const controller = useMemo(() => makeDashboardController(httpClient), []);
    return <DashboardPage controller={controller} />;
};

export const MyProfilePageFactory: React.FC = () => {
    const controller = useMemo(() => makeAuthController(httpClient), []);
    return <MyProfilePage controller={controller} />;
};

export const NotificationsPageFactory: React.FC = () => {
    const controller = useMemo(() => makeNotificationController(httpClient), []);
    return <NotificationsPage controller={controller} />;
};

export const UserListPageFactory: React.FC = () => {
    const controller = useMemo(() => makeUserController(httpClient), []);
    return <UserListPage controller={controller} />;
};

export const ClientListPageFactory: React.FC = () => {
    const controller = useMemo(() => makeClientController(httpClient), []);
    const sapConnectionController = useMemo(() => makeSapConnectionController(httpClient), []);
    const listAiModelsUseCase = useMemo(() => getListAiModelsUseCase(), []);
    return <ClientListPage controller={controller} sapConnectionController={sapConnectionController} listAiModelsUseCase={listAiModelsUseCase} />;
};

export const ProjectListPageFactory: React.FC = () => {
    const controller = useMemo(() => makeProjectController(httpClient), []);
    const clientController = useMemo(() => makeClientController(httpClient), []);
    const listAiModelsUseCase = useMemo(() => getListAiModelsUseCase(), []);
    return <ProjectListPage controller={controller} clientController={clientController} listAiModelsUseCase={listAiModelsUseCase} />;
};

export const ProjectCreatePageFactory: React.FC = () => {
    return <Navigate to={ROUTES.PROJECTS} replace />;
};

export const ProjectEditPageFactory: React.FC = () => {
    return <Navigate to={ROUTES.PROJECTS} replace />;
};

export const ReasonListPageFactory: React.FC = () => {
    const controller = useMemo(() => makeReasonController(httpClient), []);
    return <ReasonListPage controller={controller} />;
};

export const AppearancePageFactory: React.FC = () => {
    const controller = useMemo(() => makeThemeController(httpClient), []);
    return <AppearancePage controller={controller} />;
};

export const ReportsPageFactory: React.FC = () => {
    const controller = useMemo(() => makeProjectController(httpClient), []);
    return <ReportsPage controller={controller} />;
};

export const DevelopmentListPageFactory: React.FC = () => {
    const controller = useMemo(() => makeDevelopmentController(httpClient), []);
    return <DevelopmentListPage controller={controller} />;
};

export const DevelopmentFormPageFactory: React.FC = () => {
    const controller = useMemo(() => makeDevelopmentController(httpClient), []);
    const projectController = useMemo(() => makeProjectController(httpClient), []);
    return <DevelopmentFormPage controller={controller} projectController={projectController} />;
};

export const DevelopmentDetailPageFactory: React.FC = () => {
    const devController = useMemo(() => makeDevelopmentController(httpClient), []);
    const detailController = useMemo(() => makeDevelopmentDetailController(httpClient), []);
    const reasonController = useMemo(() => makeReasonController(httpClient), []);
    const projectController = useMemo(() => makeProjectController(httpClient), []);
    const aiChatRepository = useMemo(() => makeAiChatRepository(httpClient), []);
    return (
        <DevelopmentDetailPage
            devController={devController}
            detailController={detailController}
            reasonController={reasonController}
            projectController={projectController}
            aiChatRepository={aiChatRepository}
        />
    );
};
